/**
 * 设置面板 + 图鉴面板（S12 §6.4 wireframe / S10 §2.3）。
 * 由 main 在引导期调用 initPanels 装配；面板只读取设置 / 图鉴状态，不持有业务状态。
 */

import { SettingsStore, type ColorblindMode } from '../core/settings';
import { CodexStore, type CodexEntry } from '../core/codex';
import { SaveStore } from '../core/save';
import { GEM_SPECS } from '../render/palette';
import { drawGem } from '../render/gem';
import { silhouette } from '../render/geometry';
import { showToast, type HudHandles } from './hud';

interface InitOpts {
  settings: SettingsStore;
  codex: CodexStore;
  save: SaveStore;
  hud: HudHandles;
}

/** S12 §6.4 wireframe：色盲模式仅两类实用档（红绿 / 蓝黄）；三型枚举的其余值预留 */
const CB_OPTIONS: { value: ColorblindMode; label: string }[] = [
  { value: 'off', label: '关' },
  { value: 'deuteranopia', label: '红绿色盲' },
  { value: 'tritanopia', label: '蓝黄色盲' },
];

export function initPanels(opts: InitOpts): void {
  const { settings, codex, save, hud } = opts;
  const app = document.getElementById('app');
  if (!app) return;

  const btnSettings = document.getElementById('btn-settings') as HTMLButtonElement | null;
  const btnCodex = document.getElementById('btn-codex') as HTMLButtonElement | null;
  const codexBadge = document.getElementById('codex-badge') as HTMLElement | null;

  // ── 容器 ──
  const settingsModal = document.createElement('div');
  settingsModal.id = 'settings-modal';
  settingsModal.className = 'panel-modal hidden';
  app.appendChild(settingsModal);

  const codexModal = document.createElement('div');
  codexModal.id = 'codex-modal';
  codexModal.className = 'panel-modal hidden';
  app.appendChild(codexModal);

  let codexOpen = false;

  const refreshCodexBadge = (): void => {
    if (codexBadge) codexBadge.hidden = !codex.hasNew();
  };

  const openSettings = (): void => { renderSettings(); settingsModal.classList.remove('hidden'); };
  const closeSettings = (): void => { settingsModal.classList.add('hidden'); };
  const openCodex = (): void => { codex.openView(); refreshCodexBadge(); renderCodex(); codexModal.classList.remove('hidden'); codexOpen = true; };
  const closeCodex = (): void => { codexModal.classList.add('hidden'); codexOpen = false; };

  btnSettings?.addEventListener('click', openSettings);
  btnCodex?.addEventListener('click', openCodex);
  settingsModal.addEventListener('click', (e) => { if (e.target === settingsModal) closeSettings(); });
  codexModal.addEventListener('click', (e) => { if (e.target === codexModal) closeCodex(); });

  // ── 设置面板 ──
  function renderSettings(): void {
    const reducedResolved = settings.getResolved('reducedMotion');
    const locked = settings.isLocked('reducedMotion');
    const cb = settings.get('colorblindMode');

    settingsModal.innerHTML = `
      <div class="panel-card">
        <div class="panel-title">设置</div>
        <div class="settings-row">
          <div class="settings-label">
            <div class="settings-name">减少动效</div>
            <div class="settings-sub">${locked ? '自动降级 · 性能保护' : '关闭非星空特效的位移 / 闪烁 / 粒子'}</div>
          </div>
          <button id="set-reduced" class="toggle ${reducedResolved ? 'on' : ''} ${locked ? 'locked' : ''}" aria-label="减少动效开关">
            <span class="toggle-knob"></span>
          </button>
        </div>
        <div class="settings-row">
          <div class="settings-label">
            <div class="settings-name">性能档位</div>
            <div class="settings-sub">${locked ? '检测到设备性能受限，已自动降低画质与动效' : '自动判定（只读）'}</div>
          </div>
          <div class="settings-readonly">自动</div>
        </div>
        <div class="settings-row">
          <div class="settings-label">
            <div class="settings-name">色盲模式</div>
            <div class="settings-sub">切换棋子调色板映射（关闭也能玩）</div>
          </div>
          <select id="set-colorblind" class="seg">
            ${CB_OPTIONS.map((o) => `<option value="${o.value}" ${cb === o.value ? 'selected' : ''}>${o.label}</option>`).join('')}
          </select>
        </div>
        <div class="settings-row">
          <div class="settings-label">
            <div class="settings-name">文本缩放</div>
            <div class="settings-sub">跟随系统字号（只读）</div>
          </div>
          <div class="settings-readonly">跟随系统</div>
        </div>
        <div class="panel-actions">
          <button id="set-reset" class="btn">恢复默认设置</button>
          <button id="set-close" class="btn btn-primary">完成</button>
        </div>
      </div>`;

    const toggle = settingsModal.querySelector('#set-reduced') as HTMLButtonElement | null;
    toggle?.addEventListener('click', () => {
      if (locked) {
        showToast(hud, '当前为低端机自动降级（性能保护），暂不可手动关闭；设备恢复正常后可随时调整。');
        return;
      }
      const next = !settings.getResolved('reducedMotion');
      settings.set('reducedMotion', next);
    });
    const sel = settingsModal.querySelector('#set-colorblind') as HTMLSelectElement | null;
    sel?.addEventListener('change', () => {
      settings.set('colorblindMode', sel.value as ColorblindMode);
    });
    settingsModal.querySelector('#set-reset')?.addEventListener('click', () => {
      settings.resetAll();
      showToast(hud, '已恢复默认设置');
    });
    settingsModal.querySelector('#set-close')?.addEventListener('click', closeSettings);
  }

  // ── 图鉴面板 ──
  function renderCodex(): void {
    const count = codex.getUnlockedCount();
    codexModal.innerHTML = `
      <div class="panel-card codex-card">
        <div class="panel-title">星球图鉴 <span class="codex-progress">${count}/6</span></div>
        <div class="codex-grid" id="codex-grid"></div>
        <div class="panel-actions">
          <button id="codex-close" class="btn btn-primary">关闭</button>
        </div>
      </div>`;

    const grid = codexModal.querySelector('#codex-grid') as HTMLElement;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const { entry, unlocked } of codex.getEntries()) {
      const cell = document.createElement('button');
      cell.className = 'codex-cell' + (unlocked ? '' : ' locked');
      const cv = document.createElement('canvas');
      const size = 56;
      cv.width = size * dpr; cv.height = size * dpr;
      cv.style.width = `${size}px`; cv.style.height = `${size}px`;
      const ctx = cv.getContext('2d');
      if (ctx) {
        ctx.scale(dpr, dpr);
        const id = entry.planetId as keyof typeof GEM_SPECS;
        if (unlocked) {
          drawGem(ctx, id, { cx: size / 2, cy: size / 2, r: size * 0.42 });
        } else {
          // 同轮廓剪影（S10 §2.3：形状冗余，不靠颜色区分）
          const shape = GEM_SPECS[id].shape;
          const pts = silhouette(shape, size / 2, size / 2, size * 0.42);
          ctx.beginPath();
          pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
          ctx.closePath();
          ctx.fillStyle = '#3A4258';
          ctx.fill();
        }
      }
      const name = document.createElement('div');
      name.className = 'codex-name';
      name.textContent = unlocked ? entry.name : '???';
      cell.appendChild(cv);
      cell.appendChild(name);
      cell.addEventListener('click', () => {
        if (unlocked) showDetail(entry);
        else showToast(hud, entry.lockedHint);
      });
      grid.appendChild(cell);
    }
    codexModal.querySelector('#codex-close')?.addEventListener('click', closeCodex);
  }

  function showDetail(entry: CodexEntry): void {
    const size = 120;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    codexModal.innerHTML = `
      <div class="panel-card codex-card">
        <div class="codex-detail-gem" id="codex-detail-gem"></div>
        <div class="codex-detail-name">${entry.name}</div>
        <div class="codex-detail-gemname">${entry.gemName}</div>
        <div class="codex-detail-copy">${entry.copy}</div>
        <div class="panel-actions">
          <button id="codex-back" class="btn btn-primary">返回</button>
        </div>
      </div>`;
    const holder = codexModal.querySelector('#codex-detail-gem') as HTMLElement;
    const cv = document.createElement('canvas');
    cv.width = size * dpr; cv.height = size * dpr;
    cv.style.width = `${size}px`; cv.style.height = `${size}px`;
    const ctx = cv.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      drawGem(ctx, entry.planetId as keyof typeof GEM_SPECS, { cx: size / 2, cy: size / 2, r: size * 0.44 });
    }
    holder.appendChild(cv);
    codexModal.querySelector('#codex-back')?.addEventListener('click', renderCodex);
  }

  // ── S9 存档变更 → 刷新红点 / 开放中的图鉴网格 ──
  save.subscribe((p) => {
    if (p.scope === 'codex') {
      refreshCodexBadge();
      if (codexOpen) renderCodex();
    }
  });
  refreshCodexBadge();

  // 设置变更 → 若设置面板正在打开则实时刷新（S12 §6.4 / E8）
  settings.subscribe(() => {
    if (!settingsModal.classList.contains('hidden')) renderSettings();
  });
}
